import React, { useState, useEffect } from 'react'
import { dataService } from '@/lib/dataService'
import { useAuth } from '@/contexts/AuthContext'

export const TeacherClassroomsList: React.FC = () => {
  const { user, role } = useAuth()
  const [classrooms, setClassrooms] = useState<any[]>([])
  const [students, setStudents] = useState<any[]>([])
  const [assignments, setAssignments] = useState<any[]>([])
  const [subjects, setSubjects] = useState<any[]>([])
  const [scores, setScores] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  
  // Selection / Modal state
  const [selectedClassroom, setSelectedClassroom] = useState<any | null>(null)
  const [filterMyClassrooms, setFilterMyClassrooms] = useState(false)

  const loadData = async () => {
    setLoading(true)
    try {
      const [allClassrooms, allStudents, allAssignments, allSubjects, allScores] = await Promise.all([
        dataService.getClassrooms(),
        dataService.getStudents(),
        dataService.getTeachingAssignments(),
        dataService.getSubjects(),
        dataService.getScores()
      ])
      setClassrooms(allClassrooms)
      setStudents(allStudents)
      setAssignments(allAssignments)
      setSubjects(allSubjects)
      setScores(allScores)
    } catch (err) {
      console.error('Error loading classroom list:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  // Resolve progress details for Pre-Test and Post-Test
  const getProgressStats = (classroomId: number) => {
    const studentsInClass = students.filter(s => s.classroom_id === classroomId)
    const targetAssigns = assignments.filter(a => a.classroom_id === classroomId && (role !== 'admin' ? a.teacher_id === user?.id : true))
    
    if (studentsInClass.length === 0 || targetAssigns.length === 0) {
      return { hasData: false, total: 0, preFilled: 0, preLeft: 0, postFilled: 0, postLeft: 0, prePercent: 0, postPercent: 0 }
    }
    
    let preCount = 0
    let postCount = 0
    
    studentsInClass.forEach(st => {
      targetAssigns.forEach(assign => {
        const match = scores.find(sc => sc.student_id === st.id && sc.subject_id === assign.subject_id)
        if (match) {
          if (match.pre_test_score !== null && match.pre_test_score !== undefined) preCount++
          if (match.post_test_score !== null && match.post_test_score !== undefined) postCount++
        }
      })
    })
    
    const totalStudents = studentsInClass.length
    const totalPossible = totalStudents * targetAssigns.length
    const preFilled = Math.round(preCount / targetAssigns.length)
    const postFilled = Math.round(postCount / targetAssigns.length)
    
    return {
      hasData: true,
      total: totalStudents,
      preFilled: preFilled,
      preLeft: Math.max(0, totalStudents - preFilled),
      postFilled: postFilled,
      postLeft: Math.max(0, totalStudents - postFilled),
      prePercent: Math.round((preCount / totalPossible) * 100),
      postPercent: Math.round((postCount / totalPossible) * 100)
    }
  }

  // Resolve subjects taught by this teacher in a classroom
  const getTaughtSubjects = (classroomId: number) => {
    if (!user) return []
    const myAssigns = assignments.filter(a => a.teacher_id === user.id && a.classroom_id === classroomId)
    return myAssigns.map(a => {
      const sub = subjects.find(s => s.id === a.subject_id)
      return sub ? `${sub.code} (${sub.name})` : '—'
    })
  }

  // Sort function: asc by grade_level, then by room number
  const sortClassrooms = (list: any[]) => {
    return [...list].sort((a, b) => {
      if (a.grade_level !== b.grade_level) {
        return a.grade_level - b.grade_level
      }
      const roomA = a.room_number.split('/')
      const roomB = b.room_number.split('/')
      const numA = parseInt(roomA[roomA.length - 1], 10) || 0
      const numB = parseInt(roomB[roomB.length - 1], 10) || 0
      return numA - numB
    })
  }

  // Grouping classrooms
  const myClassroomIds = user ? assignments.filter(a => a.teacher_id === user.id).map(a => a.classroom_id) : []
  const isTeacher = role !== 'admin'
  const filteredClassrooms = classrooms.filter(c => {
    if (isTeacher || filterMyClassrooms) {
      return myClassroomIds.includes(c.id)
    }
    return true
  })

  const sortedList = sortClassrooms(filteredClassrooms)
  
  // Categorize: ม.ต้น (grade_level 7-9, 20) vs ม.ปลาย (grade_level 10-12, 30)
  const juniorHigh = sortedList.filter(c => c.grade_level === 20 || (c.grade_level >= 7 && c.grade_level <= 9))
  const seniorHigh = sortedList.filter(c => c.grade_level === 30 || (c.grade_level >= 10 && c.grade_level <= 12))

  const getClassTitle = (classroom: any) => {
    if (classroom.grade_level === 20) return `ม.ต้น (ทั่วไป)`
    if (classroom.grade_level === 30) return `ม.ปลาย (ทั่วไป)`
    const displayGrade = classroom.grade_level >= 7 ? classroom.grade_level - 6 : classroom.grade_level
    return `ชั้นมัธยมศึกษาปีที่ ${displayGrade}/${classroom.room_number.split('/')[1] || classroom.room_number}`
  }


  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid rgba(255, 255, 255, 0.1)',
          borderTopColor: 'var(--primary)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`
          @keyframes spin { to { transform: rotate(360deg); } }
        `}</style>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '8px' }}>🏫 ข้อมูลห้องเรียนทั้งหมด</h1>
          <p style={{ color: 'var(--text-secondary)' }}>เรียกดูรายชื่อนักเรียนและข้อมูลการเรียนการสอนรายชั้นเรียน</p>
        </div>
        
        {/* Toggle buttons for Admin */}
        {!isTeacher && (
          <div style={{ display: 'flex', gap: '8px', background: '#f1f5f9', padding: '4px', borderRadius: '8px' }}>
            <button 
              type="button"
              onClick={() => setFilterMyClassrooms(false)}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                border: 'none',
                cursor: 'pointer',
                background: !filterMyClassrooms ? '#ffffff' : 'transparent',
                color: !filterMyClassrooms ? 'var(--primary)' : 'var(--text-secondary)',
                boxShadow: !filterMyClassrooms ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                transition: 'all 0.2s'
              }}
            >
              🏫 ห้องเรียนทั้งหมด
            </button>
            <button 
              type="button"
              onClick={() => setFilterMyClassrooms(true)}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                border: 'none',
                cursor: 'pointer',
                background: filterMyClassrooms ? '#ffffff' : 'transparent',
                color: filterMyClassrooms ? 'var(--primary)' : 'var(--text-secondary)',
                boxShadow: filterMyClassrooms ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                transition: 'all 0.2s'
              }}
            >
              📚 ห้องที่คุณสอน
            </button>
          </div>
        )}
      </div>

      {/* JUNIOR HIGH GRID */}
      <div>
        <h3 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '16px', borderLeft: '4px solid var(--primary)', paddingLeft: '12px' }}>
          ระดับมัธยมศึกษาตอนต้น (ม.1 - ม.3)
        </h3>
        {juniorHigh.length === 0 ? (
          <div style={{ background: 'var(--bg-card)', padding: '24px', textAlign: 'center', borderRadius: '12px', border: '1px solid var(--border)', color: 'var(--text-muted)' }}>
            ไม่มีห้องเรียนแสดงผล
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '20px' }}>
            {juniorHigh.map(c => {
              const studentsInClass = students.filter(s => s.classroom_id === c.id)
              const taughtSubs = getTaughtSubjects(c.id)
              const isTaught = taughtSubs.length > 0

              return (
                <div 
                  key={c.id} 
                  onClick={() => setSelectedClassroom({ ...c, students: studentsInClass })}
                  className="glassmorphism"
                  style={{
                    padding: '20px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    position: 'relative',
                    border: isTaught ? '1.5px solid var(--primary)' : '1px solid var(--border)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)'
                    e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.05)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)'
                    e.currentTarget.style.boxShadow = 'none'
                  }}
                >
                  {isTaught && (
                    <span style={{ position: 'absolute', top: '12px', right: '12px', background: 'rgba(79,70,229,0.1)', color: 'var(--primary)', padding: '2px 8px', borderRadius: '12px', fontSize: '10px', fontWeight: '700' }}>
                      📚 คุณสอนห้องนี้
                    </span>
                  )}
                  <h4 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '8px', color: 'var(--text-primary)' }}>
                    ม.{c.grade_level >= 7 ? c.grade_level - 6 : c.grade_level}/{c.room_number}
                  </h4>
                  <div style={{ fontSize: '13px', color: 'var(--text-secondary)', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <div>👥 จำนวนนักเรียน: <strong style={{ color: 'var(--text-primary)' }}>{studentsInClass.length}</strong> คน</div>
                    
                    {/* PROGRESS BARS */}
                    {(() => {
                      const stats = getProgressStats(c.id)
                      if (!stats.hasData) return null
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '6px', borderTop: '1px solid rgba(0,0,0,0.06)', paddingTop: '6px' }}>
                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10.5px', marginBottom: '2px', color: 'var(--text-secondary)' }}>
                              <span>📝 Pre-Test: <strong>{stats.preFilled}/{stats.total}</strong> คน</span>
                              <span style={{ fontWeight: '600' }}>เหลือ {stats.preLeft} คน</span>
                            </div>
                            <div style={{ width: '100%', height: '5px', background: 'rgba(0,0,0,0.05)', borderRadius: '2.5px', overflow: 'hidden' }}>
                              <div style={{ width: `${stats.prePercent}%`, height: '100%', background: 'linear-gradient(90deg, #3b82f6, #60a5fa)', borderRadius: '2.5px' }} />
                            </div>
                          </div>
                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10.5px', marginBottom: '2px', color: 'var(--text-secondary)' }}>
                              <span>🎓 Post-Test: <strong>{stats.postFilled}/{stats.total}</strong> คน</span>
                              <span style={{ fontWeight: '600' }}>เหลือ {stats.postLeft} คน</span>
                            </div>
                            <div style={{ width: '100%', height: '5px', background: 'rgba(0,0,0,0.05)', borderRadius: '2.5px', overflow: 'hidden' }}>
                              <div style={{ width: `${stats.postPercent}%`, height: '100%', background: 'linear-gradient(90deg, #10b981, #34d399)', borderRadius: '2.5px' }} />
                            </div>
                          </div>
                        </div>
                      )
                    })()}

                    {isTaught && (
                      <div style={{ fontSize: '12px', color: 'var(--primary)', marginTop: '6px', fontWeight: '500' }}>
                        📖 วิชาที่สอน: {taughtSubs.join(', ')}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* SENIOR HIGH GRID */}
      <div>
        <h3 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '16px', borderLeft: '4px solid var(--secondary)', paddingLeft: '12px' }}>
          ระดับมัธยมศึกษาตอนปลาย (ม.4 - ม.6)
        </h3>
        {seniorHigh.length === 0 ? (
          <div style={{ background: 'var(--bg-card)', padding: '24px', textAlign: 'center', borderRadius: '12px', border: '1px solid var(--border)', color: 'var(--text-muted)' }}>
            ไม่มีห้องเรียนแสดงผล
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '20px' }}>
            {seniorHigh.map(c => {
              const studentsInClass = students.filter(s => s.classroom_id === c.id)
              const taughtSubs = getTaughtSubjects(c.id)
              const isTaught = taughtSubs.length > 0

              return (
                <div 
                  key={c.id} 
                  onClick={() => setSelectedClassroom({ ...c, students: studentsInClass })}
                  className="glassmorphism"
                  style={{
                    padding: '20px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    position: 'relative',
                    border: isTaught ? '1.5px solid var(--secondary)' : '1px solid var(--border)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)'
                    e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.05)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)'
                    e.currentTarget.style.boxShadow = 'none'
                  }}
                >
                  {isTaught && (
                    <span style={{ position: 'absolute', top: '12px', right: '12px', background: 'rgba(234,179,8,0.1)', color: '#b45309', padding: '2px 8px', borderRadius: '12px', fontSize: '10px', fontWeight: '700' }}>
                      📚 คุณสอนห้องนี้
                    </span>
                  )}
                  <h4 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '8px', color: 'var(--text-primary)' }}>
                    ม.{c.grade_level >= 7 ? c.grade_level - 6 : c.grade_level}/{c.room_number}
                  </h4>
                  <div style={{ fontSize: '13px', color: 'var(--text-secondary)', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <div>👥 จำนวนนักเรียน: <strong style={{ color: 'var(--text-primary)' }}>{studentsInClass.length}</strong> คน</div>
                    
                    {/* PROGRESS BARS */}
                    {(() => {
                      const stats = getProgressStats(c.id)
                      if (!stats.hasData) return null
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '6px', borderTop: '1px solid rgba(0,0,0,0.06)', paddingTop: '6px' }}>
                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10.5px', marginBottom: '2px', color: 'var(--text-secondary)' }}>
                              <span>📝 Pre-Test: <strong>{stats.preFilled}/{stats.total}</strong> คน</span>
                              <span style={{ fontWeight: '600' }}>เหลือ {stats.preLeft} คน</span>
                            </div>
                            <div style={{ width: '100%', height: '5px', background: 'rgba(0,0,0,0.05)', borderRadius: '2.5px', overflow: 'hidden' }}>
                              <div style={{ width: `${stats.prePercent}%`, height: '100%', background: 'linear-gradient(90deg, #3b82f6, #60a5fa)', borderRadius: '2.5px' }} />
                            </div>
                          </div>
                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10.5px', marginBottom: '2px', color: 'var(--text-secondary)' }}>
                              <span>🎓 Post-Test: <strong>{stats.postFilled}/{stats.total}</strong> คน</span>
                              <span style={{ fontWeight: '600' }}>เหลือ {stats.postLeft} คน</span>
                            </div>
                            <div style={{ width: '100%', height: '5px', background: 'rgba(0,0,0,0.05)', borderRadius: '2.5px', overflow: 'hidden' }}>
                              <div style={{ width: `${stats.postPercent}%`, height: '100%', background: 'linear-gradient(90deg, #10b981, #34d399)', borderRadius: '2.5px' }} />
                            </div>
                          </div>
                        </div>
                      )
                    })()}

                    {isTaught && (
                      <div style={{ fontSize: '12px', color: '#b45309', marginTop: '6px', fontWeight: '500' }}>
                        📖 วิชาที่สอน: {taughtSubs.join(', ')}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* STUDENT LIST POPUP MODAL */}
      {selectedClassroom && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 100,
          padding: '20px'
        }}>
          <div className="glassmorphism" style={{ padding: '32px', maxWidth: '800px', width: '100%', background: '#ffffff', border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: '20px', maxHeight: '90vh' }}>
            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--text-primary)' }}>
                  📋 {getClassTitle(selectedClassroom)}
                </h3>
                <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>รายชื่อนักเรียนและกลุ่มการเรียนการสอน</p>
              </div>
              <button 
                type="button"
                onClick={() => setSelectedClassroom(null)}
                style={{ background: 'transparent', border: 'none', fontSize: '24px', cursor: 'pointer', color: 'var(--text-secondary)' }}
              >
                ✕
              </button>
            </div>

            {/* Students Table */}
            <div style={{ flex: 1, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: '12px' }}>
              {selectedClassroom.students.length === 0 ? (
                <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                  📭 ไม่พบรายชื่อนักเรียนในห้องเรียนนี้
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '1px solid var(--border)', color: 'var(--text-secondary)' }}>
                      <th style={{ padding: '12px 16px', width: '80px' }}>เลขที่</th>
                      <th style={{ padding: '12px 16px', width: '150px' }}>รหัสประจำตัว</th>
                      <th style={{ padding: '12px 16px' }}>ชื่อ-นามสกุล</th>
                      <th style={{ padding: '12px 16px', width: '150px' }}>ประเภทห้อง</th>
                      <th style={{ padding: '12px 16px', width: '100px' }}>สถานะ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...selectedClassroom.students]
                      .sort((a, b) => (a.number_in_class || 1) - (b.number_in_class || 1))
                      .map(s => (
                        <tr key={s.id} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={{ padding: '12px 16px', fontWeight: '600' }}>{s.number_in_class || '—'}</td>
                          <td style={{ padding: '12px 16px', color: 'var(--text-secondary)' }}>{s.student_id_card}</td>
                          <td style={{ padding: '12px 16px', fontWeight: '500' }}>{s.title}{s.first_name} {s.last_name}</td>
                          <td style={{ padding: '12px 16px' }}>
                            <span style={{ fontSize: '12px', padding: '2px 8px', borderRadius: '4px', background: '#f1f5f9', border: '1px solid #e2e8f0' }}>
                              {s.room_type || 'Normal'}
                            </span>
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: '600',
                              color: s.status === 'active' ? 'green' : s.status === 'resigned' ? 'red' : 'orange',
                              background: s.status === 'active' ? 'rgba(34,197,94,0.1)' : s.status === 'resigned' ? 'rgba(239,68,68,0.1)' : 'rgba(249,115,22,0.1)',
                              display: 'inline-block'
                            }}>
                              {s.status === 'active' ? 'กำลังเรียน' : s.status === 'resigned' ? 'ลาออก' : 'ย้ายสถานศึกษา'}
                            </span>
                          </td>
                        </tr>
                      ))
                    }
                  </tbody>
                </table>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid var(--border)', paddingTop: '16px' }}>
              <button 
                type="button" 
                onClick={() => setSelectedClassroom(null)}
                className="btn btn-outline"
              >
                ปิดหน้าต่าง
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
export default TeacherClassroomsList
